import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Link,
  Button,
  Divider,
} from '@mui/material';
import { ArrowBack, Policy, Security } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ServiceAddendumPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mb: 3 }}
          >
            Back
          </Button>

          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Policy sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              HFSS Club Management
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary' }}>
              Service Addendum
            </Typography>
          </Box>

          <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: 'info.light' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              This service operates as clubs.hikeandfly.app and is an integral part of the HFSS platform.
              All data processing is governed by our main{' '}
              <Link href="https://www.hikeandfly.app/privacy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://www.hikeandfly.app/terms" target="_blank" rel="noopener">
                Terms of Service
              </Link>.
            </Typography>
          </Paper>

          <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security /> Data Processing Specifics
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            The HFSS Club Management system processes the following categories of data:
          </Typography>
          
          <Box component="ul" sx={{ mb: 4 }}>
            <li>
              <Typography variant="body1">
                <strong>Membership Records:</strong> Personal information, contact details, and club associations
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Flight Credentials:</strong> License numbers, medical certificates, and qualifications
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Insurance Documentation:</strong> Policy verification and coverage details
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Event Participation:</strong> Registrations, shuttle bookings, and attendance records
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Financial Records:</strong> Payment status and membership fees (no payment processing)
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Communications:</strong> Club announcements and member correspondence
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 3 }}>
            Integration with Main Platform
          </Typography>
          
          <Box component="ul" sx={{ mb: 4 }}>
            <li>
              <Typography variant="body1">
                Optional linking with HFSS flight tracking accounts for competition participation
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Shared authentication system across all HFSS services
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Unified privacy settings and consent management
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Cross-platform data portability upon request
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 3 }}>
            Club-Specific Rights
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            In addition to all rights outlined in our main Privacy Policy, club members have:
          </Typography>
          
          <Box component="ul" sx={{ mb: 4 }}>
            <li>
              <Typography variant="body1">
                Right to export club-specific data via API endpoint <code>/api/v1/my-data</code>
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Right to delete club associations while maintaining flight tracking data
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Right to manage club-specific consent preferences independently
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Right to request data portability between clubs
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 3 }}>
            Data Retention
          </Typography>
          
          <Box component="ul" sx={{ mb: 4 }}>
            <li>
              <Typography variant="body1">
                <strong>Active Members:</strong> Data retained for duration of membership
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Former Members:</strong> Basic records retained for 2 years
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Financial Records:</strong> 10 years per Swiss legal requirements
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Safety Records:</strong> 5 years per aviation regulations
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h5" sx={{ mb: 3 }}>
            Contact Information
          </Typography>
          
          <Box sx={{ bgcolor: 'grey.100', p: 3, borderRadius: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Data Controller:</strong> Simone Severini (via HFSS)
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Service:</strong> HFSS Club Management
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Privacy Inquiries:</strong>{' '}
              <Link href="mailto:privacy@hikeandfly.app">privacy@hikeandfly.app</Link>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Support:</strong>{' '}
              <Link href="mailto:clubs@hikeandfly.app">clubs@hikeandfly.app</Link>
            </Typography>
            <Typography variant="body1">
              <strong>Data Subject Requests:</strong>{' '}
              <Link href="mailto:dsr@hikeandfly.app">dsr@hikeandfly.app</Link>
            </Typography>
          </Box>

          <Box sx={{ mt: 4, p: 3, bgcolor: 'primary.light', borderRadius: 2 }}>
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'white' }}>
              All terms and conditions pursuant to{' '}
              <Link 
                href="https://www.hikeandfly.app/privacy" 
                target="_blank"
                sx={{ color: 'white', textDecoration: 'underline' }}
              >
                https://www.hikeandfly.app/privacy
              </Link>
              {' '}and{' '}
              <Link 
                href="https://www.hikeandfly.app/terms" 
                target="_blank"
                sx={{ color: 'white', textDecoration: 'underline' }}
              >
                https://www.hikeandfly.app/terms
              </Link>
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ServiceAddendumPage;